// Company profile service - handles company profile data operations.
// HTTP and ApiError mapping come from the shared apiClient.
import type {
  ProfileValue,
  ProfileValueDTO,
  CompanyProfileResponse,
  RepeaterInstanceDTO,
} from './types';
import { apiRequest, apiRequestData } from '../http/apiClient';

/**
 * Save/update company profile field values in bulk
 * Upserts values for the given company
 */
export async function saveProfileValues(
  companyId: string,
  values: ProfileValue[]
): Promise<ProfileValueDTO[]> {
  // This endpoint answers with a bare array (no { data } envelope).
  return apiRequest<ProfileValueDTO[]>(`/api/v1/companies/${companyId}/profile/values/bulk`, {
    method: 'PUT',
    body: JSON.stringify({ values }),
  });
}

// Create a new repeater instance (row) for a repeater field
export async function createRepeaterInstance(
  companyId: string,
  fieldId: string
): Promise<RepeaterInstanceDTO> {
  const data = await apiRequestData<(RepeaterInstanceDTO & { instanceId?: string }) | undefined>(
    `/api/v1/companies/${companyId}/profile/repeater/${fieldId}/instances`,
    { method: 'POST' }
  );
  // Older BE responses name the id `instanceId`.
  return { ...data, id: data?.id ?? data?.instanceId } as RepeaterInstanceDTO;
}

// Update field editorial status (IN_PROGRESS, AI_REVIEW, MARKED_FOR_REVIEW, VALIDATED)
export async function updateFieldStatus(
  companyId: string,
  valueId: string,
  status: string
): Promise<void> {
  await apiRequest(`/api/v1/companies/${companyId}/profile/values/${valueId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

// Delete a repeater instance by ID
export async function deleteRepeaterInstance(companyId: string, instanceId: string): Promise<void> {
  await apiRequest(`/api/v1/companies/${companyId}/profile/instances/${instanceId}`, {
    method: 'DELETE',
  });
}

// Delete a single profile value by its valueId
export async function deleteProfileValue(companyId: string, valueId: string): Promise<void> {
  await apiRequest(`/api/v1/companies/${companyId}/profile/values/${valueId}`, {
    method: 'DELETE',
  });
}

/**
 * Delete a company by ID.
 * Only the company OWNER can delete it (403 otherwise).
 */
export async function deleteCompany(companyId: string): Promise<void> {
  await apiRequest(`/api/v1/companies/${companyId}`, { method: 'DELETE' });
}

/**
 * Upload a file as a profile field value (e.g. pitch video, pitch deck).
 * Sends multipart/form-data. Backend saves to S3 and stores the URL as a profile value.
 * If profileFieldId is omitted, backend appends the file to the profile_assets field.
 */
export async function uploadProfileFile(
  companyId: string,
  file: File,
  profileFieldId?: string,
  repeaterInstanceId?: string
): Promise<{ value: string }> {
  const formData = new FormData();
  formData.append('file', file);
  if (profileFieldId) formData.append('profileFieldId', profileFieldId);
  if (repeaterInstanceId) formData.append('repeaterInstanceId', repeaterInstanceId);

  // Also send as query params — some backends read multipart fields inconsistently
  const params = new URLSearchParams();
  if (profileFieldId) params.append('profileFieldId', profileFieldId);
  if (repeaterInstanceId) params.append('repeaterInstanceId', repeaterInstanceId);

  // FormData body: the client leaves Content-Type to the browser (multipart boundary).
  // The BE answers the field's full URL list; the stored value is that list as JSON.
  const result = await apiRequestData<{ urls?: unknown }>(
    `/api/v1/companies/${companyId}/profile/values/file?${params}`,
    { method: 'POST', body: formData }
  );
  if (!Array.isArray(result.urls)) {
    throw new Error('Upload response without a URL list');
  }
  return { value: JSON.stringify(result.urls) };
}

/**
 * Fetch company profile with all field values
 * Returns sections with fields and their current values
 */
export async function fetchCompanyProfile(companyId: string): Promise<CompanyProfileResponse> {
  return apiRequest<CompanyProfileResponse>(`/api/v1/companies/${companyId}/profile`);
}
